import { useCallback, useEffect, useState } from 'react';
import { ChatChannelResponse } from '@/lib/api';
import { ChannelListItem } from './ChannelListItem';

function byRecentActivity(a: ChatChannelResponse, b: ChatChannelResponse): number {
  const timeA = a.lastMessage?.createdAt ?? a.updatedAt;
  const timeB = b.lastMessage?.createdAt ?? b.updatedAt;
  return timeB.localeCompare(timeA);
}

function pinnedFirst(a: ChatChannelResponse, b: ChatChannelResponse): number {
  const pinA = a.isPinned ? 1 : 0;
  const pinB = b.isPinned ? 1 : 0;
  if (pinB !== pinA) return pinB - pinA;
  return byRecentActivity(a, b);
}

interface ChannelListProps {
  channels: ChatChannelResponse[];
  activeChannelId: string | null;
  onSelectChannel: (channelId: string) => void;
  loading: boolean;
  viewMode?: 'sections' | 'unified';
}

function SectionLabel({ label }: { label: string }) {
  return (
    <div className="px-4 py-2 text-[10px] font-normal text-muted-foreground uppercase tracking-wider">
      {label}
    </div>
  );
}

export function ChannelList({
  channels: initialChannels,
  activeChannelId,
  onSelectChannel,
  loading,
  viewMode = 'sections',
}: ChannelListProps) {
  const [pinOverrides, setPinOverrides] = useState<Record<string, boolean>>({});

  const channels = initialChannels.map((c) =>
    c.channelId in pinOverrides ? { ...c, isPinned: pinOverrides[c.channelId] } : c,
  );

  // Clear overrides when the server data has caught up, or when a channel leaves the list
  useEffect(() => {
    setPinOverrides((prev) => {
      if (Object.keys(prev).length === 0) return prev;
      const channelById = new Map(initialChannels.map((c) => [c.channelId, c]));
      const next = { ...prev };
      let changed = false;
      for (const id of Object.keys(next)) {
        const ch = channelById.get(id);
        if (!ch || next[id] === ch.isPinned) {
          delete next[id];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [initialChannels]);

  const handlePinToggled = useCallback((channelId: string, isPinned: boolean) => {
    setPinOverrides((prev) => ({ ...prev, [channelId]: isPinned }));
  }, []);

  const handlePinFailed = useCallback((channelId: string) => {
    setPinOverrides((prev) => {
      const next = { ...prev };
      delete next[channelId];
      return next;
    });
  }, []);

  if (loading) {
    return (
      <div className="p-4 space-y-3">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="animate-pulse flex items-center gap-3 p-2">
            <div className="h-10 w-10 bg-muted rounded-full" />
            <div className="flex-1">
              <div className="h-4 bg-muted rounded w-24 mb-2" />
              <div className="h-3 bg-muted rounded w-32" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (channels.length === 0) {
    return <div className="p-4 text-center text-sm text-muted-foreground">No channels yet</div>;
  }

  const communityChannels = channels.filter((c) => c.channelType === 'global');
  const guildChannels = channels.filter((c) => c.channelType === 'guild').sort(pinnedFirst);
  const groupChannels = channels.filter((c) => c.channelType === 'group').sort(pinnedFirst);
  const directChannels = channels.filter((c) => c.channelType === 'direct').sort(pinnedFirst);

  const renderItem = (channel: ChatChannelResponse) => (
    <ChannelListItem
      key={channel.channelId}
      channel={channel}
      isActive={channel.channelId === activeChannelId}
      onClick={() => onSelectChannel(channel.channelId)}
      onPinToggled={handlePinToggled}
      onPinFailed={handlePinFailed}
    />
  );

  if (viewMode === 'unified') {
    // Community always first; within guilds/groups/DMs pinned items float to top
    const ordered = [...communityChannels, ...guildChannels, ...groupChannels, ...directChannels];
    return (
      <div className="flex flex-col h-full overflow-y-auto">
        <div className="p-2 flex flex-col gap-1">{ordered.map(renderItem)}</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full overflow-y-auto gap-4">
      {communityChannels.length > 0 && (
        <div className="p-2">
          <div className="mt-2">
            <SectionLabel label="Community" />
          </div>
          <div className="flex flex-col gap-1">{communityChannels.map(renderItem)}</div>
        </div>
      )}
      {guildChannels.length > 0 && (
        <div className="p-2">
          <SectionLabel label="Guilds" />
          <div className="flex flex-col gap-1">{guildChannels.map(renderItem)}</div>
        </div>
      )}
      {groupChannels.length > 0 && (
        <div className="p-2">
          <SectionLabel label="Groups" />
          <div className="flex flex-col gap-1">{groupChannels.map(renderItem)}</div>
        </div>
      )}
      {directChannels.length > 0 && (
        <div className="p-2">
          <SectionLabel label="Direct Messages" />
          <div className="flex flex-col gap-1">{directChannels.map(renderItem)}</div>
        </div>
      )}
    </div>
  );
}
